import { TrendDown, TrendUp } from '@phosphor-icons/react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { Card } from '../ui/Card';
import { ExpensesSectionLabel } from '../expenses/ExpensesSectionLabel';
import { cn } from '../../utils/cn';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { buildJourneyLinePoints } from '../../lib/selfieDisplayUtils';

function format(template, params) {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    params[key] === undefined ? `{${key}}` : String(params[key]),
  );
}

/**
 * Secção "A tua jornada" — heading + cartão de linha (não barras).
 * .pen: p0z6v (heading `ds/label/section-heading` + `ds/card/metric-sparkline`, a0lhr)
 */
export function SelfieJourneySparkline({
  timeline = [],
  snapshotCount = 0,
  className,
  isLoading = false,
}) {
  const countHint =
    snapshotCount === 1
      ? pt.snapshots.history.journeyCountOne
      : format(pt.snapshots.history.journeyCountMany, { count: snapshotCount });

  if (isLoading) {
    return (
      <section className={cn('flex flex-col gap-space-3', className)} aria-busy="true">
        <div className="h-3 w-32 animate-pulse rounded bg-muted" />
        <Card className="flex flex-col gap-space-4">
          <div className="h-4 w-40 animate-pulse rounded bg-muted" />
          <div className="h-8 w-28 animate-pulse rounded bg-muted" />
          <div className="h-16 w-full animate-pulse rounded bg-muted" />
        </Card>
      </section>
    );
  }

  const { points, labels, latestValue, pctChange } = buildJourneyLinePoints(timeline);
  if (points.length < 2) return null;

  const isUp = pctChange !== null && pctChange >= 0;
  const TrendIcon = isUp ? TrendUp : TrendDown;

  return (
    <section className={cn('flex flex-col gap-space-3', className)}>
      <ExpensesSectionLabel
        title={pt.snapshots.history.journeyEyebrow}
        hint={countHint}
        hintAlways
      />
      <Card data-ds-id="ds/card/metric-sparkline" className="flex flex-col gap-space-4">
        <div className="flex items-center justify-between gap-space-3">
          <p className="text-type-title-card font-semibold text-card-foreground">
            {pt.snapshots.history.sparklineLabel}
          </p>
          {pctChange !== null ? (
            <span
              className={cn(
                'inline-flex items-center gap-space-1 rounded-ds-sm px-space-2 py-space-1 text-type-chip font-bold',
                isUp ? 'bg-sage-tint text-sage-on-tint' : 'bg-warning-tint text-warning-fg',
              )}
            >
              <TrendIcon className="size-3" weight="bold" aria-hidden />
              {pctChange >= 0 ? '+' : ''}
              {pctChange.toFixed(1).replace('.', ',')} %
            </span>
          ) : null}
        </div>

        <div className="flex items-end gap-space-3">
          <span className="font-display text-type-display-kpi font-bold text-card-foreground">
            {formatEUR(latestValue)}
          </span>
          <span className="pb-0.5 text-type-body-sm text-muted-foreground">
            {pt.snapshots.history.sparklineLatest}
          </span>
        </div>

        <div className="-mx-space-5 -mb-space-5 h-16" aria-hidden>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              accessibilityLayer={false}
              data={points}
              margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="selfieJourneySparkline" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#7C9E6F" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#7C9E6F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke="#7C9E6F"
                strokeWidth={2.5}
                fill="url(#selfieJourneySparkline)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="flex justify-between">
          {labels.map((label, index) => (
            <span
              key={index}
              className="text-type-chip text-muted-foreground"
            >
              {label}
            </span>
          ))}
        </div>
      </Card>
    </section>
  );
}

export default SelfieJourneySparkline;
